/**
 * Utilitaires pour agréger et traiter les statistiques financières multi-devises
 */

/**
 * Agrège les statistiques de toutes les devises disponibles
 * @param {Array} availableCurrencies - Liste des devises avec leurs données basiques
 * @param {Function} getStatisticsForCurrency - Fonction pour récupérer les stats d'une devise
 * @returns {Promise<Object>} Statistiques globales agrégées
 */
export const aggregateAllCurrenciesStats = async (availableCurrencies, getStatisticsForCurrency) => {
  try {
    if (!availableCurrencies || availableCurrencies.length === 0) {
      return createEmptyGlobalStats();
    }

    console.log('🌍 [GLOBAL STATS] Agrégation des statistiques pour', availableCurrencies.length, 'devises');

    // Récupérer les statistiques détaillées pour chaque devise
    const currencyStatsPromises = availableCurrencies.map(async (currencyData) => {
      try {
        const stats = await getStatisticsForCurrency(currencyData.currency);
        return {
          currency: currencyData.currency,
          basicData: currencyData,
          detailedStats: stats
        };
      } catch (error) {
        console.warn(`Erreur lors de la récupération des stats pour ${currencyData.currency}:`, error);
        return {
          currency: currencyData.currency,
          basicData: currencyData,
          detailedStats: null,
          error: error.message
        };
      }
    });

    const allCurrencyStats = await Promise.all(currencyStatsPromises);
    console.log('📊 [GLOBAL STATS] Données récupérées:', allCurrencyStats);

    // Agréger toutes les données
    const globalStats = aggregateStatistics(allCurrencyStats);
    
    console.log('✅ [GLOBAL STATS] Agrégation terminée:', globalStats);
    return globalStats;

  } catch (error) {
    console.error('❌ [GLOBAL STATS] Erreur d\'agrégation:', error);
    throw error;
  }
};

/**
 * Agrège les statistiques de plusieurs devises
 * @param {Array} allCurrencyStats - Données de toutes les devises
 * @returns {Object} Statistiques globales
 */
const aggregateStatistics = (allCurrencyStats) => {
  const globalStats = {
    currencies_included: [],
    currencies_with_errors: [],
    total_currencies: allCurrencyStats.length,
    
    // Statistiques globales de réservations
    global_reservations: {
      total_count: 0,
      by_status_global: {},
      by_currency: []
    },
    
    // Statistiques globales de transactions  
    global_transactions: {
      total_count: 0,
      by_status_global: {},
      by_currency: [],
      largest_transaction: {
        amount: 0,
        currency: null,
        details: null
      }
    },
    
    // Statistiques globales de dépôts
    global_deposits: {
      total_count: 0,
      by_status_global: {},
      by_currency: [],
      total_amounts_by_currency: []
    },
    
    // Période d'activité globale
    global_activity: {
      has_activity: false,
      first_transaction: null,
      last_transaction: null,
      active_currencies_count: 0
    },
    
    // Métadonnées
    metadata: {
      generated_at: new Date().toISOString(),
      successful_currencies: 0,
      failed_currencies: 0
    }
  };

  // Traitement de chaque devise
  allCurrencyStats.forEach(currencyInfo => {
    if (currencyInfo.error || !currencyInfo.detailedStats) {
      globalStats.currencies_with_errors.push({
        currency: currencyInfo.currency,
        error: currencyInfo.error || 'Données manquantes'
      });
      globalStats.metadata.failed_currencies++;
      return;
    }

    const currency = currencyInfo.currency;
    const stats = currencyInfo.detailedStats;
    const basicData = currencyInfo.basicData;

    globalStats.currencies_included.push(currency);
    globalStats.metadata.successful_currencies++;

    // Agrégation des réservations
    if (stats.reservations_statistics) {
      const resStats = stats.reservations_statistics;
      globalStats.global_reservations.total_count += resStats.total_count || 0;
      
      // Ajout par devise
      globalStats.global_reservations.by_currency.push({
        currency: currency,
        count: resStats.total_count || 0,
        by_status: resStats.by_status || []
      });
      
      // Agrégation par statut global
      if (resStats.by_status && Array.isArray(resStats.by_status)) {
        resStats.by_status.forEach(statusItem => {
          const status = statusItem.status;
          if (!globalStats.global_reservations.by_status_global[status]) {
            globalStats.global_reservations.by_status_global[status] = 0;
          }
          globalStats.global_reservations.by_status_global[status] += statusItem.count || 0;
        });
      }
    }

    // Agrégation des transactions
    if (stats.transactions_statistics) {
      const transStats = stats.transactions_statistics;
      globalStats.global_transactions.total_count += transStats.total_count || 0;
      
      // Ajout par devise
      globalStats.global_transactions.by_currency.push({
        currency: currency,
        count: transStats.total_count || 0,
        by_status: transStats.by_status || [],
        largest_transaction: transStats.largest_transaction || 0
      });
      
      // Agrégation par statut global
      if (transStats.by_status && Array.isArray(transStats.by_status)) {
        transStats.by_status.forEach(statusItem => {
          const status = statusItem.status;
          if (!globalStats.global_transactions.by_status_global[status]) {
            globalStats.global_transactions.by_status_global[status] = 0;
          }
          globalStats.global_transactions.by_status_global[status] += statusItem.count || 0;
        });
      }
      
      // Plus grande transaction globale
      const currentLargest = transStats.largest_transaction || 0;
      if (currentLargest > globalStats.global_transactions.largest_transaction.amount) {
        globalStats.global_transactions.largest_transaction = {
          amount: currentLargest,
          currency: currency,
          details: stats.metadata?.largest_transaction_details || null
        };
      }
    }

    // Agrégation des dépôts
    if (stats.deposits_statistics) {
      const depStats = stats.deposits_statistics;
      globalStats.global_deposits.total_count += depStats.total_count || 0;
      
      let currencyDepositTotal = 0;
      
      // Ajout par devise
      const currencyDeposits = {
        currency: currency,
        count: depStats.total_count || 0,
        by_status: depStats.by_status || [],
        total_amount: 0
      };
      
      // Agrégation par statut global et calcul du total
      if (depStats.by_status && Array.isArray(depStats.by_status)) {
        depStats.by_status.forEach(statusItem => {
          const status = statusItem.status;
          const amount = statusItem.total_amount || 0;
          
          if (!globalStats.global_deposits.by_status_global[status]) {
            globalStats.global_deposits.by_status_global[status] = 0;
          }
          globalStats.global_deposits.by_status_global[status] += statusItem.count || 0;
          
          currencyDepositTotal += amount;
        });
      }
      
      currencyDeposits.total_amount = currencyDepositTotal;
      globalStats.global_deposits.by_currency.push(currencyDeposits);
      
      if (currencyDepositTotal > 0) {
        globalStats.global_deposits.total_amounts_by_currency.push({
          currency: currency,
          total_amount: currencyDepositTotal
        });
      }
    }

    // Activité globale
    if (stats.activity_period) {
      const activity = stats.activity_period;
      
      if (activity.has_activity) {
        globalStats.global_activity.active_currencies_count++;
        globalStats.global_activity.has_activity = true;
        
        // Première transaction globale
        if (activity.first_transaction && 
            (!globalStats.global_activity.first_transaction || 
             new Date(activity.first_transaction) < new Date(globalStats.global_activity.first_transaction))) {
          globalStats.global_activity.first_transaction = activity.first_transaction;
        }
        
        // Dernière transaction globale
        if (activity.last_transaction && 
            (!globalStats.global_activity.last_transaction || 
             new Date(activity.last_transaction) > new Date(globalStats.global_activity.last_transaction))) {
          globalStats.global_activity.last_transaction = activity.last_transaction;
        }
      }
    }
  });

  // Conversion des objets by_status_global en tableaux pour l'affichage
  globalStats.global_reservations.by_status_array = Object.entries(globalStats.global_reservations.by_status_global)
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => b.count - a.count);

  globalStats.global_transactions.by_status_array = Object.entries(globalStats.global_transactions.by_status_global)
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => b.count - a.count);

  globalStats.global_deposits.by_status_array = Object.entries(globalStats.global_deposits.by_status_global)
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => b.count - a.count);

  return globalStats;
};

/**
 * Crée une structure de statistiques globales vide
 * @returns {Object} Structure vide
 */
const createEmptyGlobalStats = () => ({
  currencies_included: [],
  currencies_with_errors: [],
  total_currencies: 0,
  global_reservations: {
    total_count: 0,
    by_status_global: {},
    by_status_array: [],
    by_currency: []
  },
  global_transactions: {
    total_count: 0,
    by_status_global: {},
    by_status_array: [],
    by_currency: [],
    largest_transaction: { amount: 0, currency: null, details: null }
  },
  global_deposits: {
    total_count: 0,
    by_status_global: {},
    by_status_array: [],
    by_currency: [],
    total_amounts_by_currency: []
  },
  global_activity: {
    has_activity: false,
    first_transaction: null,
    last_transaction: null,
    active_currencies_count: 0
  },
  metadata: {
    generated_at: new Date().toISOString(),
    successful_currencies: 0,
    failed_currencies: 0
  }
});

/**
 * Formate une valeur monétaire pour l'affichage global
 * @param {number} amount - Montant
 * @param {string} currency - Devise
 * @returns {string} Montant formaté
 */
export const formatGlobalAmount = (amount, currency) => {
  if (!amount || amount === 0) return '0';
  
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `${amount} ${currency}`;
  }
};

/**
 * Calcule le pourcentage d'une valeur par rapport au total
 * @param {number} value - Valeur
 * @param {number} total - Total
 * @returns {string} Pourcentage formaté
 */
export const calculatePercentage = (value, total) => {
  if (!total || total === 0) return '0%';
  const percentage = (value / total) * 100;
  return `${percentage.toFixed(1)}%`;
};

export default {
  aggregateAllCurrenciesStats,
  formatGlobalAmount,
  calculatePercentage
};